import { SaveOutlined } from '@ant-design/icons';
import { Col, Form, Input, message, Modal, Row } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import CountryInput from '@/components/FormItems/CountryInput';
import { BlobStorageContainerType } from '@/constants';
import CreateBtn from '@/components/app/CreateBtn';
import SingleImageUpload from '@/components/FormItems/SingleImageUpload';
import i18n from '@/helpers/i18n';
import { CompanyAccountDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

const columns: IColumnType<CompanyAccountDto>[] = [
  {
    title: 'base.Country',
    dataIndex: 'countryName',
  },
  {
    title: 'companySettingsPage_CompanyDisplayName',
    dataIndex: 'companyDisplayName',
  },
  {
    title: 'companySettingsPage_CompanyAddress',
    dataIndex: 'companyAddress',
  },
  {
    title: 'companySettingsPage_CompanyLogoImageUrl',
    dataIndex: 'companyLogoImageUrl',
    width: 150,
    align: 'center',
    render: v =>
      v ? (
        <a href={v} target="_blank" rel="noreferrer">
          view
        </a>
      ) : (
        <></>
      ),
  },
];

const filters: Array<IFilterItem> = [
  { type: 'SELECT', name: 'CountryId', label: 'base.Country', customElm: <CountryInput placeholder={i18n.t('base.Country') as any} /> },
  { type: 'TEXT', name: 'companyDisplayName', label: 'companySettingsPage_CompanyDisplayName' },
];

const TicketSettingTab = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const createEntity = values => {
    setLoading(true);
    apiSdk.TransportTicketSettingService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.TransportTicketSettingService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div>
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/commons/transport-ticket-settings/get-transport-ticket-settings"
        bordered
        order_by="createdOn"
        order_by_asc={true}
        allowEdit={true}
        onEdit={record => {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...record,
          });
        }}
        allowDelete={true}
        delete_url="/commons/transport-ticket-settings/delete"
        filters={filters}
        searchBtnAlignLeft
        extraBtns={
          <CreateBtn
            onClick={() => {
              setIsNewForm(true);
              setFormVisible(true);
              form.resetFields();
            }}
          />
        }
      ></ZTable>
      {formVisible ? (
        <Modal
          visible={formVisible}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={isNewForm ? t('companySettingsPage_CreateTicketSetting') : t('companySettingsPage_UpdateTicketSetting')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateEntity(values);
                } else {
                  createEntity(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={24}>
                <Form.Item label={t('base.Country')} name="countryId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <CountryInput />
                </Form.Item>

                <Form.Item
                  label={t('companySettingsPage_CompanyDisplayName')}
                  name="companyDisplayName"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  label={t('companySettingsPage_CompanyAddress')}
                  name="companyAddress"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item label={t('companySettingsPage_CompanyLogoImageUrl')} name="companyLogoImageUrl">
                  <SingleImageUpload blobStorageContainer={BlobStorageContainerType.CompaniesContainer} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      ) : null}
    </div>
  );
};
export default TicketSettingTab;
